import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Leaf, HeartPulse, ArrowRight, Award } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { Button } from "../components/Button";
import { CERTIFICATIONS, IMAGES } from "../data";

const ICONS = {
  "ISO 9001": Award,
  "ISO 14001": Leaf,
  "ISO 45001": HeartPulse,
};

export default function Certifications() {
  return (
    <div data-testid="certifications-page">
      <PageHero
        eyebrow="Quality & Compliance"
        title="Certifications."
        subtitle="At MRL Transports, quality, safety and environmental responsibility are integral to how we operate. Our ISO-certified management systems reflect this commitment."
        image={IMAGES.certifications}
      />

      <section className="py-24 md:py-32 bg-white" data-testid="cert-cards">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CERTIFICATIONS.map((c, i) => {
              const Icon = ICONS[c.code] || ShieldCheck;
              return (
                <Reveal key={c.code} delay={i * 120}>
                  <div className="relative bg-brand-smoke p-10 border-t-4 border-brand-green h-full overflow-hidden" data-testid={`cert-card-${i}`}>
                    <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-brand-orange/10" />
                    <div className="relative">
                      <div className="w-16 h-16 flex items-center justify-center bg-brand-green">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <p className="font-heading text-5xl text-brand-green leading-none mt-8">{c.code}</p>
                      <p className="font-heading text-xl mt-3 text-brand-ink">{c.title}</p>
                      <p className="font-body text-sm text-zinc-600 mt-4 leading-relaxed">{c.body}</p>

                      <div className="mt-8 flex items-center gap-2 border-t border-zinc-300 pt-4">
                        <CheckCircle2 className="w-4 h-4 text-brand-green" />
                        <p className="font-mono text-xs uppercase tracking-widest text-brand-green">Certified</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section className="py-24 md:py-32 bg-brand-smoke" data-testid="cert-commitments">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <Reveal><p className="section-tag">Our Commitments</p></Reveal>
              <Reveal delay={100}>
                <h2 className="font-heading text-4xl md:text-6xl leading-[0.95] tracking-tightest mt-6 text-brand-ink">
                  Standards that shape our operations.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:pt-8">
              <Reveal delay={200}>
                <p className="font-body text-lg text-zinc-700 leading-relaxed">
                  Certifications reflect our commitment to maintaining international standards across every aspect of our business —
                  from driver training and vehicle maintenance to environmental impact and workplace safety.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-zinc-300">
            {[
              { t: "Quality Assurance", d: "Documented SOPs, quality audits and performance reviews for every project." },
              { t: "Environmental Care", d: "Fuel-efficient fleet, emissions monitoring and waste management practices." },
              { t: "Safety First", d: "Trained crew, defensive-driving programmes and site-specific safety protocols." },
            ].map((x, i) => (
              <Reveal key={x.t} delay={i * 100}>
                <div className="p-10 border-r border-b border-zinc-300 bg-white" data-testid={`cert-commitment-${i}`}>
                  <p className="font-mono text-xs text-brand-orange">0{i + 1}</p>
                  <p className="font-heading text-2xl mt-4 text-brand-ink tracking-tight">{x.t}</p>
                  <p className="font-body text-sm text-zinc-600 mt-3 leading-relaxed">{x.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-green text-white" data-testid="cert-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-heading text-3xl md:text-5xl leading-[0.95] tracking-tightest">
              Certified processes.<br />Trusted outcomes.
            </h2>
          </div>
          <div className="lg:col-span-4">
            <Link to="/contact" data-testid="cert-cta-contact">
              <Button variant="secondary" size="lg" className="w-full">Discuss Your Project <ArrowRight className="w-4 h-4" /></Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
